// The 2D Otto card (design 7.5): the approved front portrait
// (Robots/transparent/ottofront.png, cropped by scripts/prepare-images.mjs) with
// a ring that follows his voice, blinks from a lid overlay, and the Listening,
// Thinking, Speaking line. It is voice-only mode's Otto, the loading poster in
// video mode, and the fallback when WebGL fails, so there is never a blank stage.
// No three.js here.
import { useEffect, useRef, type ReactNode } from "react";
import type { OttoState } from "../call/ottoState";

/** Eye centres and radius on the card image (600 x 720), measured Sept 28 2026. */
export const CARD_EYES = Object.freeze({
  width: 600,
  height: 720,
  left: { x: 213, y: 164 },
  right: { x: 322, y: 164 },
  radius: 28,
});

const BLINK = Object.freeze({ minGap: 2000, maxGap: 6000, close: 70, hold: 30, open: 150 });

export interface OttoCard2DProps {
  state: OttoState;
  /** Otto's voice level, 0..1, read every frame (no re-render). */
  levelRef?: { current: number };
  /** The status line under the card; omit when the call screen shows it elsewhere. */
  statusLabel?: string | null;
  compact?: boolean;
  reducedMotion?: boolean;
  /** Lines under the status (voice only: the AI disclosure and "keep this screen open"). */
  children?: ReactNode;
}

/**
 * Lid overlay position. The card frame is a square as wide as the image (the
 * 600 x 720 portrait overflows the circle at the bottom), so both axes are in
 * units of the image WIDTH; consult.css offsets the image and the eyes by the
 * same 2 percent from the top.
 */
export function eyeStyle(eye: { x: number; y: number }) {
  const { width, radius } = CARD_EYES;
  return {
    left: `${((eye.x - radius) / width) * 100}%`,
    top: `${((eye.y - radius) / width) * 100}%`,
    width: `${((radius * 2) / width) * 100}%`,
    height: `${((radius * 2) / width) * 100}%`,
  };
}

export function OttoCard2D({
  state,
  levelRef,
  statusLabel,
  compact = false,
  reducedMotion = false,
  children,
}: OttoCard2DProps) {
  const ringRef = useRef<HTMLDivElement>(null);
  const lidRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (typeof requestAnimationFrame !== "function") return undefined;
    let frame = 0;
    let nextBlink = performance.now() + 1800;
    let blinkStart = -1;
    let smoothed = 0;
    const tick = (now: number) => {
      // The ring follows his voice.
      const level = Math.max(0, Math.min(1, levelRef?.current ?? 0));
      smoothed += (level - smoothed) * 0.35;
      const ring = ringRef.current;
      if (ring) {
        const scale = reducedMotion ? 1 : 1 + smoothed * 0.08;
        ring.style.transform = `scale(${scale.toFixed(4)})`;
        ring.style.opacity = String(0.35 + Math.min(0.65, smoothed * 1.4));
      }
      // Blinks: a quick close, a moment shut, a slower open, every 2 to 6 s.
      let closed = 0;
      if (blinkStart < 0 && now >= nextBlink) blinkStart = now;
      if (blinkStart >= 0) {
        const age = now - blinkStart;
        if (age < BLINK.close) closed = age / BLINK.close;
        else if (age < BLINK.close + BLINK.hold) closed = 1;
        else if (age < BLINK.close + BLINK.hold + BLINK.open)
          closed = 1 - (age - BLINK.close - BLINK.hold) / BLINK.open;
        else {
          blinkStart = -1;
          nextBlink = now + BLINK.minGap + Math.random() * (BLINK.maxGap - BLINK.minGap);
        }
      }
      // Thinking: lids a touch lower (design 7.2).
      const rest = stateRef.current === "thinking" ? 0.18 : 0;
      const lid = Math.max(closed, rest);
      for (const node of lidRefs.current) {
        if (node) node.style.transform = `translateY(${(-100 + lid * 100).toFixed(2)}%)`;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [levelRef, reducedMotion]);

  return (
    <div className={`otto-card${compact ? " otto-card--compact" : ""} is-${state}`} data-otto-state={state}>
      <div className="otto-card__frame">
        <div className="otto-card__ring" ref={ringRef} aria-hidden="true" />
        <div className="otto-card__portrait">
          <img
            src="/images/consult/otto-card.webp"
            alt="Otto, Otto's Office's manager"
            width={CARD_EYES.width}
            height={CARD_EYES.height}
          />
          {[CARD_EYES.left, CARD_EYES.right].map((eye, index) => (
            <span className="otto-card__eye" style={eyeStyle(eye)} key={index} aria-hidden="true">
              <span
                className="otto-card__lid"
                ref={(node) => {
                  lidRefs.current[index] = node;
                }}
              />
            </span>
          ))}
        </div>
      </div>
      {statusLabel ? (
        <p className="otto-card__status">
          <span aria-hidden="true" />
          {statusLabel}
        </p>
      ) : null}
      {children}
    </div>
  );
}
